import { uuidv7 } from "./queue.ts";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Lesson, Exercise } from "@ca/shared";
import { Button, Chip, PageHead, Skeleton } from "@ca/ui";
import { useRevisionStudy } from "./use-study.ts";
import { api, HttpError } from "./api.ts";
import { Html, Failed, Pages, useRead, useWrite, mediaUrl } from "./revision-ui.tsx";
export interface RevisionProps {
  uid: string;
  notify: (text: string) => void;
  theme?: string | undefined;
}
export function Explanation({ value }: { value: unknown }) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return <Html value={value} />;
  if (Array.isArray(value))
    return (
      <>
        {value.map((x: unknown, i: number) => (
          <Explanation key={i} value={x} />
        ))}
      </>
    );
  if (typeof value === "object")
    return (
      <>
        {Object.entries(value as Record<string, unknown>)
          .filter(
            ([key]) =>
              ![
                "schema_version",
                "schemaVersion",
                "raw",
                "part",
                "correct",
                "source",
                "kind",
                "key",
              ].includes(key),
          )
          .map(([key, v]) => (
            <section key={key}>
              <b>
                {(
                  {
                    structure: "Cấu trúc",
                    tips: "Mẹo",
                    whyHtml: "Giải thích",
                    extraHtml: "Ghi chú",
                    translation: "Bản dịch",
                    question: "Câu hỏi",
                    options: "Các phương án",
                    tipsHtml: "Mẹo",
                    summary: "Cấu trúc",
                    vocab: "Từ vựng",
                  } as Record<string, string>
                )[key] ?? ""}
              </b>
              <Explanation value={v} />
            </section>
          ))}
      </>
    );
  return <p>{typeof value === "number" || typeof value === "boolean" ? String(value) : ""}</p>;
}
const GROUPS: [string, string, string, string][] = [
  ["p5", "Part 5", "🧩", "yellow"],
  ["p6", "Part 6", "📝", "pink"],
  ["p7", "Part 7", "📖", "sage"],
  ["trap", "Bẫy thường gặp", "🪤", "blue"],
];
function LessonProgress({ l }: { l: Lesson }) {
  return l.done > 0 ? (
    <div className="lt-prog">
      <span className="tr">
        <i style={{ width: `${l.total ? (l.done / l.total) * 100 : 0}%` }} />
      </span>
      <small>
        đã làm {l.done}/{l.total}
      </small>
    </div>
  ) : (
    <small>{l.total} câu luyện</small>
  );
}
/** Lý thuyết — `S.grammar` của bản mẫu (spec 10): bảng 4 cột, bấm một bài để đọc. */
export function Theory({ uid, notify, theme }: RevisionProps) {
  const [key, setKey] = useState<string | null>(null);
  const q = useRead(uid, "/v1/lessons", z.array(Lesson));
  if (key)
    return (
      <LessonReader
        key={key}
        uid={uid}
        notify={notify}
        theme={theme}
        lessonKey={key}
        total={q.data?.length}
        close={() => {
          setKey(null);
        }}
      />
    );
  return (
    <>
      <PageHead
        space="gram"
        title="Lý thuyết"
        desc={`${q.data?.length ?? 30} bài ngữ pháp và mẹo làm bài, viết gọn để tra nhanh. Mỗi bài kèm câu luyện ngay bên cạnh — đọc xong làm luôn.`}
      />
      <Failed error={q.error} />
      {q.isPending && <Skeleton />}
      <div className="lt-board">
        {GROUPS.map(([group, title, icon, tone]) => {
          const items = (q.data ?? [])
            .filter((l) => l.groupKey === group)
            .sort((a, b) => a.sort - b.sort);
          return (
            <section key={group} className="lt-col" aria-label={title}>
              <div className={`lt-head ${tone}`}>
                <span aria-hidden="true">{icon}</span>
                <b>{title}</b>
                <span className="lt-n">· {items.length} bài</span>
              </div>
              {items.map((l) => (
                <button
                  key={l.key}
                  className="lt-item"
                  onClick={() => {
                    setKey(l.key);
                  }}
                >
                  <b>{l.title}</b>
                  {(l.locked || l.wrong > 0) && (
                    <span className="row" style={{ gap: 6 }}>
                      {l.locked && <Chip tone="pro">🔒 Pro</Chip>}
                      {l.wrong > 0 && <Chip tone="bad">bạn đang sai {l.wrong} câu</Chip>}
                    </span>
                  )}
                  <LessonProgress l={l} />
                </button>
              ))}
            </section>
          );
        })}
      </div>
      <p className="faint" style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2 }}>
        ⏺ Xem danh sách chưa tính giờ; bấm vào một bài để đọc thì mới bắt đầu tính (nhóm “Ngữ pháp”
        trong báo cáo).
      </p>
    </>
  );
}
function splitHtml(html: string) {
  const root = document.createElement("div");
  root.innerHTML = html;
  const pages: string[] = [];
  let current = "";
  for (const child of Array.from(root.children)) {
    if (current.length + child.outerHTML.length > 2400 && current) {
      pages.push(current);
      current = "";
    }
    current += child.outerHTML;
  }
  if (current) pages.push(current);
  return pages.length ? pages : [html];
}
/** Trang một bài (`ltLessonHTML`): đọc bên trái, luyện bên phải. Dùng lại cho bài trị lỗi. */
export function LessonReader({
  uid,
  notify,
  lessonKey,
  close,
  remedy = false,
  total,
}: RevisionProps & {
  lessonKey: string;
  close: () => void;
  remedy?: boolean;
  total?: number | undefined;
}) {
  const q = useRead(uid, `/v1/lessons/${encodeURIComponent(lessonKey)}`, Lesson);
  const [page, setPage] = useState(0);
  const [noteId, setNoteId] = useState(() => {
    const key = `ca-lesson-note:${uid}:${lessonKey}`;
    const id = sessionStorage.getItem(key) ?? crypto.randomUUID();
    sessionStorage.setItem(key, id);
    return id;
  });
  const [mode, setMode] = useState<"gram" | "fix">(remedy ? "fix" : "gram");
  const pages = useMemo(() => splitHtml(q.data?.html ?? ""), [q.data?.html]);
  const write = useWrite(uid, notify);
  useRevisionStudy(
    uid,
    "lesson",
    mode === "fix" ? undefined : q.data?.studyRefId,
    "gram",
    lessonKey,
  );
  useEffect(() => {
    if (q.data)
      void api(`/v1/me/lessons/${encodeURIComponent(lessonKey)}/open`, {
        method: "PUT",
        key: uuidv7(),
        accountId: uid,
      }).catch(() => {});
  }, [uid, lessonKey, !!q.data]);
  const group = GROUPS.find(([g]) => g === (q.data?.groupKey ?? lessonKey.split("|")[0]));
  const missing = q.error instanceof HttpError && q.error.status === 404;
  const title = q.data?.title ?? lessonKey.split("|").slice(1).join("|");
  return (
    <>
      <Failed error={missing ? null : q.error} />
      {!remedy && (
        <section className="card lt-top">
          <StudyBar stop={close} />
          <div className="stack" style={{ gap: 8 }}>
            <Button variant="line" size="sm" style={{ alignSelf: "flex-start" }} onClick={close}>
              ← {total ?? 30} bài lý thuyết
            </Button>
            <div className="row" style={{ gap: 8 }}>
              {group && (
                <span className={`lt-chip ${group[3]}`}>
                  {group[2]} {group[1]}
                </span>
              )}
              {q.data && q.data.wrong > 0 && (
                <span className="chip bad" data-tip="Lỗi của bạn ở dạng này|theo Sổ tay lỗi sai">
                  bạn đang sai {q.data.wrong} câu
                </span>
              )}
            </div>
            <h1 className="lt-title">{title}</h1>
            {q.data && (
              <div className="lt-meta">
                <LessonProgress l={q.data} />
              </div>
            )}
          </div>
        </section>
      )}
      <div className="lt-lesson">
        <section
          className="card lt-read"
          onPointerDown={() => {
            setMode("gram");
          }}
          onFocus={() => {
            setMode("gram");
          }}
        >
          {remedy && <h2>{title}</h2>}
          {missing ? (
            <p className="muted">
              Kho cũ chưa có lý thuyết riêng cho dạng nghe này. Ôn các câu ở bên cạnh, rồi đọc lời
              giải gốc sau khi chọn đáp án.
            </p>
          ) : (
            <div className="lt-html">
              <Html value={pages[page]} />
            </div>
          )}
          <Pages offset={page} total={pages.length} limit={1} set={setPage} />
        </section>
        <aside
          className="lt-side card"
          onPointerDown={() => {
            setMode("fix");
          }}
          onFocus={() => {
            setMode("fix");
          }}
        >
          <Exercises
            uid={uid}
            notify={notify}
            kind={lessonKey.startsWith("trap|") ? "trap" : "skill"}
            itemKey={lessonKey.replace(/^trap\|/, "")}
            track={mode === "fix"}
          />
          {!remedy && (
            <Button
              variant="soft"
              data-tip="Ghi vào Take note|Lưu ý chính của bài thành một ghi chú"
              onClick={() =>
                void write(`/v1/me/lessons/${encodeURIComponent(lessonKey)}/note`, "PUT", {
                  id: noteId,
                })
                  .then(() => {
                    notify("Đã ghi bài này vào Take note.");
                    const next = crypto.randomUUID();
                    sessionStorage.setItem(`ca-lesson-note:${uid}:${lessonKey}`, next);
                    setNoteId(next);
                  })
                  .catch(() => {})
              }
            >
              🗒️ Ghi vào Take note
            </Button>
          )}
        </aside>
      </div>
    </>
  );
}
/** Thanh "đang tính giờ học · ■ Đọc xong" (`sessBar` của bản mẫu). */
function StudyBar({ stop }: { stop: () => void }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const update = (e: Event) => {
      setOn((e as CustomEvent<{ state: string }>).detail.state === "recording");
    };
    window.addEventListener("ca-study-status", update);
    return () => {
      window.removeEventListener("ca-study-status", update);
    };
  }, []);
  return (
    <div className={`sessbar${on ? "" : " paused"}`}>
      <span className="ss-dot" />
      <span className="ss-lbl">{on ? "đang tính giờ học" : "tạm dừng tính giờ"}</span>
      <span className="grow" />
      <Button variant="soft" size="sm" onClick={stop}>
        ■ Đọc xong
      </Button>
    </div>
  );
}
export function Exercises({
  uid,
  notify,
  kind,
  itemKey,
  track = true,
}: RevisionProps & { kind: "skill" | "trap"; itemKey: string; track?: boolean }) {
  const [offset, setOffset] = useState(0);
  const [reviews, setReviews] = useState<Record<string, Exercise>>({});
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const q = useRead(
    uid,
    `/v1/remedy/${kind}/${encodeURIComponent(itemKey)}?offset=${offset}&limit=5`,
    z.object({ total: z.number(), items: z.array(Exercise) }),
  );
  const write = useWrite(uid, notify);
  const settle = useRevisionStudy(uid, "remedy", track ? q.data?.items[0]?.id : undefined, "fix");
  async function grade(id: string, chosen: string) {
    setBusy(true);
    setPicked((old) => ({ ...old, [id]: chosen }));
    try {
      await settle();
      const result = z
        .object({ review: Exercise })
        .parse(await write("/v1/remedy/answers", "POST", { id, chosen }));
      setReviews((old) => ({ ...old, [id]: result.review }));
    } catch {
      /* The write helper reports failure and the form keeps its current contents. */
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Failed error={q.error} />
      {q.data?.items.map((item) => {
        const review = reviews[item.id],
          pick = picked[item.id];
        return (
          <div key={item.id} className="gl-q">
            <div className="row between">
              <span className="eyebrow">
                ✍️ Luyện ngay · câu {item.sort + 1}/{q.data.total}
              </span>
              {item.done && !review && <span className="chip">đã làm</span>}
            </div>
            {item.passageHtml && (
              <div className="gq-ex">
                <Html value={item.passageHtml} />
              </div>
            )}
            {item.audioKey && <audio controls src={mediaUrl(item.audioKey)} />}
            {item.imageKeys.map((key) => (
              <img
                key={key}
                src={mediaUrl(key)}
                alt="Hình của câu nghe"
                style={{ maxWidth: "100%" }}
              />
            ))}
            <div className="gq-s">
              <Html value={item.prompt} />
            </div>
            <div className="gl-opts" role="group" aria-label="Chọn đáp án">
              {item.options.map((o) => {
                const cls = !review
                  ? pick === o.key
                    ? " picked"
                    : ""
                  : o.key === review.correct
                    ? " right"
                    : o.key === pick
                      ? " wrong"
                      : "";
                return (
                  <button
                    key={o.key}
                    className={`ans${cls}`}
                    disabled={busy}
                    onClick={() => void grade(item.id, o.key)}
                  >
                    <span className="rd" />({o.key}){" "}
                    <span dangerouslySetInnerHTML={{ __html: o.text }} />
                  </button>
                );
              })}
            </div>
            {review && (
              <div className={`gl-why ${pick === review.correct ? "ok" : "no"}`}>
                <b>
                  {pick === review.correct
                    ? "✓ Đúng rồi"
                    : `✕ Chưa đúng · đáp án là (${review.correct ?? ""})`}
                </b>
                <Explanation value={review.explanation} />
                <Explanation value={review.translation} />
              </div>
            )}
          </div>
        );
      })}
      <Pages offset={offset} total={q.data?.total ?? 0} limit={5} set={setOffset} />
    </>
  );
}
